import { Button, ButtonGroup, IconButton, Stack } from "@chakra-ui/react";
import { get, range } from "lodash";
import { useMemo } from "react";
import {
  MdOutlineKeyboardArrowLeft,
  MdOutlineKeyboardArrowRight,
} from "react-icons/md";

const MIN_NUMBER_OF_BUTTONS = 9;
export const DEFAULT_PAGINATION = {
  page: 1,
  totalPages: 1,
  hasPrevPage: false,
  hasNextPage: false,
  nbOfButtons: MIN_NUMBER_OF_BUTTONS,
};

export default function Pagination({
  value = DEFAULT_PAGINATION,
  setValue = () => {},
  onChange = () => {},
}) {
  const changePage = (page) => {
    setValue({ ...value, page });
    onChange(page);
  };

  const buttonArray = useMemo(() => {
    const totalPages = value.totalPages;
    const currentPage = value.page;
    const _length = value.nbOfButtons ?? 0;
    const resultLength = Math.max(MIN_NUMBER_OF_BUTTONS, _length);
    const result = range(1, totalPages + 1);
    if (totalPages <= resultLength) {
      return result;
    }
    const isTruncateLeft = currentPage > resultLength - 3;
    const isTruncateRight = currentPage < totalPages - resultLength + 4;
    const isTruncateOneSide = !(isTruncateLeft && isTruncateRight);
    const reservedButtonLength = isTruncateOneSide ? 3 : 4;
    const middleButtonLength = Math.max(1, resultLength - reservedButtonLength);
    const middleButtonOffset = Math.floor((middleButtonLength - 1) / 2);
    if (!isTruncateOneSide) {
      const firstMiddleButton = currentPage - middleButtonOffset;
      const lastMiddleButton = currentPage + middleButtonOffset;
      return [
        1,
        "...",
        ...range(firstMiddleButton, lastMiddleButton + 1),
        "...",
        totalPages,
      ];
    }
    if (isTruncateLeft) {
      return [
        1,
        "...",
        ...range(totalPages - middleButtonLength, totalPages + 1),
      ];
    }
    if (isTruncateRight) {
      return [...range(1, middleButtonLength + 2), "...", totalPages];
    }
    return result;
  }, [value]);

  const hasNextPage = value.page < value.totalPages;
  const hasPrevPage = value.page > 1;

  if (get(value, "totalPages") <= 1) return null;
  return (
    <Stack
      py={4}
      gap={2}
      direction={{ base: "column", lg: "row" }}
      alignSelf="center"
    >
      <ButtonGroup size="sm">
        <IconButton
          isDisabled={!hasPrevPage}
          onClick={() => changePage(value.page - 1)}
        >
          <MdOutlineKeyboardArrowLeft fontSize="24px" />
        </IconButton>

        {buttonArray.map((button, index) => {
          const isPageNumberButton = typeof button === "number";
          return (
            <Button
              key={index}
              h={8}
              w={8}
              background={value.page === button ? "primary.500" : "white"}
              color={value.page !== button ? "primary.500" : "white"}
              border={`1px solid ${
                value.page !== button ? "#DFE3E8" : "white"
              }`}
              onClick={() => isPageNumberButton && changePage(button)}
            >
              {button}
            </Button>
          );
        })}

        <IconButton
          isDisabled={!hasNextPage}
          onClick={() => changePage(value.page + 1)}
        >
          <MdOutlineKeyboardArrowRight fontSize="24px" />
        </IconButton>
      </ButtonGroup>
    </Stack>
  );
}
